import { useEffect, useRef } from 'react';
import {
  FT_INNER,
  FT_MIDDLE,
  FT_OUTER,
  DEFAULT_ARROW_LENGTH_FT,
  METERS_PER_FOOT,
  MIN_DRAG_FT,
  computeBearingDeg,
  computeDestinationFeet,
  computeDistanceFeet,
} from './isdBlockGeometry';

const COLOR_OPPOSITE = '#22D3EE'; // cyan
const COLOR_OPPOSITE_HALO = '#000000';
const STROKE_WEIGHT_MAIN = 2;
const STROKE_WEIGHT_HALO = 4;

const DRAFT_KEY = '__draft__';

/**
 * Renders cyan opposite-block overlays for F-case ISD intersections and for the
 * in-progress form draft. Wires drag handles on the active intersection's
 * opposite block (move center, rotate/extend tip) and on the draft block.
 *
 * Saved opposite blocks are stored as `intersection.oppositePoint = { insertionPoint, azimuth, arrowLengthFeet }`.
 * The form-time draft is the same shape, lifted at RSAAnalysis level.
 *
 * Updates flow back via:
 *   - `onUpdateIntersection(id, { oppositePoint: nextBlock })` for saved blocks
 *   - `onUpdateOppositeBlockDraft(nextBlock)` for the draft
 */
export function useIsdOppositeBlockOverlays({
  map,
  pathsPanelMode,
  intersections,
  activeIntersectionId,
  oppositeBlockDraft,
  onUpdateIntersection,
  onUpdateOppositeBlockDraft,
}) {
  const overlaysRef = useRef({}); // keyed by intersection id (or DRAFT_KEY)

  const onUpdateIntersectionRef = useRef(onUpdateIntersection);
  onUpdateIntersectionRef.current = onUpdateIntersection;
  const onUpdateDraftRef = useRef(onUpdateOppositeBlockDraft);
  onUpdateDraftRef.current = onUpdateOppositeBlockDraft;

  // ── Render / sync overlays ──
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    if (pathsPanelMode !== 'intersection') {
      Object.values(overlaysRef.current).forEach((b) => removeBundle(b));
      overlaysRef.current = {};
      return;
    }
    const g = window.google.maps;

    // Build the list of {key, isActive, block} to render.
    const targets = [];
    intersections.forEach((isd) => {
      if (isd.isdCase !== 'f-case') return;
      const op = isd.oppositePoint;
      if (!op?.insertionPoint) return;
      targets.push({
        key: isd.id,
        isActive: isd.id === activeIntersectionId,
        block: op,
      });
    });
    if (oppositeBlockDraft?.insertionPoint) {
      targets.push({
        key: DRAFT_KEY,
        // The draft is always treated as active (handles visible) so the user
        // can keep adjusting it before clicking OK.
        isActive: true,
        block: oppositeBlockDraft,
      });
    }

    const live = new Set();
    targets.forEach(({ key, isActive, block }) => {
      live.add(key);
      const azimuth = Number.isFinite(block.azimuth) ? block.azimuth : 0;
      const arrowLen = Number.isFinite(block.arrowLengthFeet)
        ? block.arrowLengthFeet
        : DEFAULT_ARROW_LENGTH_FT;
      const center = block.insertionPoint;
      const tip = computeDestinationFeet(center, azimuth, arrowLen);

      let bundle = overlaysRef.current[key];
      if (!bundle) {
        bundle = createBundle(g, map);
        overlaysRef.current[key] = bundle;
      }

      bundle.innerCircle.setCenter(center);
      bundle.innerCircle.setRadius(FT_INNER * METERS_PER_FOOT);
      bundle.innerCircle.setOptions(circleOptsInner(isActive));

      bundle.middleCircle.setCenter(center);
      bundle.middleCircle.setRadius(FT_MIDDLE * METERS_PER_FOOT);
      bundle.middleCircle.setOptions(circleOptsRing());

      bundle.outerCircle.setCenter(center);
      bundle.outerCircle.setRadius(FT_OUTER * METERS_PER_FOOT);
      bundle.outerCircle.setOptions(circleOptsRing());

      bundle.innerHalo.setCenter(center);
      bundle.innerHalo.setRadius(FT_INNER * METERS_PER_FOOT);
      bundle.innerHalo.setOptions(haloOpts(isActive));
      bundle.middleHalo.setCenter(center);
      bundle.middleHalo.setRadius(FT_MIDDLE * METERS_PER_FOOT);
      bundle.middleHalo.setOptions(haloOpts(isActive));
      bundle.outerHalo.setCenter(center);
      bundle.outerHalo.setRadius(FT_OUTER * METERS_PER_FOOT);
      bundle.outerHalo.setOptions(haloOpts(isActive));

      bundle.arrow.setPath([center, tip]);
      bundle.arrow.setOptions(arrowOpts());
      bundle.arrowHalo.setPath([center, tip]);
      bundle.arrowHalo.setOptions(arrowHaloOpts(isActive));

      bundle.tipHandle.setPosition(tip);
      bundle.tipHandle.setVisible(isActive);
    });

    // Tear down stale bundles.
    Object.keys(overlaysRef.current).forEach((id) => {
      if (!live.has(id)) {
        removeBundle(overlaysRef.current[id]);
        delete overlaysRef.current[id];
      }
    });
  }, [map, pathsPanelMode, intersections, activeIntersectionId, oppositeBlockDraft]);

  // ── Drag handles for whichever block is "active" (saved active, or draft) ──
  useEffect(() => {
    if (!map || !window.google?.maps?.event) return;
    if (pathsPanelMode !== 'intersection') return;

    const g = window.google.maps;

    // Build the list of bundles that need drag listeners attached.
    // Saved opposite block of the active intersection + the draft block.
    const draggableTargets = [];
    if (activeIntersectionId) {
      const activeIsd = intersections.find((i) => i.id === activeIntersectionId);
      if (activeIsd?.isdCase === 'f-case' && activeIsd.oppositePoint?.insertionPoint) {
        const bundle = overlaysRef.current[activeIntersectionId];
        if (bundle) {
          draggableTargets.push({
            bundle,
            commit: (next) => onUpdateIntersectionRef.current?.(
              activeIntersectionId,
              { oppositePoint: { ...activeIsd.oppositePoint, ...next } },
            ),
          });
        }
      }
    }
    if (oppositeBlockDraft?.insertionPoint) {
      const bundle = overlaysRef.current[DRAFT_KEY];
      if (bundle) {
        draggableTargets.push({
          bundle,
          commit: (next) => onUpdateDraftRef.current?.({
            ...oppositeBlockDraft,
            ...next,
          }),
        });
      }
    }

    const allListeners = [];
    draggableTargets.forEach(({ bundle, commit }) => {
      // Drag inner disc → move whole block.
      const moveVisuals = (newCenter) => {
        bundle.middleCircle.setCenter(newCenter);
        bundle.outerCircle.setCenter(newCenter);
        bundle.innerHalo.setCenter(newCenter);
        bundle.middleHalo.setCenter(newCenter);
        bundle.outerHalo.setCenter(newCenter);
        const path = bundle.arrow.getPath();
        if (path && path.getLength() >= 2) {
          const tipPos = path.getAt(1);
          bundle.arrow.setPath([newCenter, tipPos]);
          bundle.arrowHalo.setPath([newCenter, tipPos]);
        }
      };
      allListeners.push(g.event.addListener(bundle.innerCircle, 'drag', () => {
        const c = bundle.innerCircle.getCenter();
        if (!c) return;
        moveVisuals({ lat: c.lat(), lng: c.lng() });
      }));
      allListeners.push(g.event.addListener(bundle.innerCircle, 'dragend', () => {
        const c = bundle.innerCircle.getCenter();
        if (!c) return;
        commit({ insertionPoint: { lat: c.lat(), lng: c.lng() } });
      }));

      // Drag tip handle → set azimuth + arrowLengthFeet.
      allListeners.push(g.event.addListener(bundle.tipHandle, 'drag', (e) => {
        if (!e.latLng) return;
        const centerPos = bundle.innerCircle.getCenter();
        if (!centerPos) return;
        const centerLl = { lat: centerPos.lat(), lng: centerPos.lng() };
        const cursor = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        bundle.arrow.setPath([centerLl, cursor]);
        bundle.arrowHalo.setPath([centerLl, cursor]);
      }));
      allListeners.push(g.event.addListener(bundle.tipHandle, 'dragend', (e) => {
        if (!e.latLng) return;
        const centerPos = bundle.innerCircle.getCenter();
        if (!centerPos) return;
        const centerLl = { lat: centerPos.lat(), lng: centerPos.lng() };
        const cursor = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        const azimuth = computeBearingDeg(centerLl, cursor);
        const arrowLengthFeet = Math.max(MIN_DRAG_FT, computeDistanceFeet(centerLl, cursor));
        commit({ azimuth, arrowLengthFeet });
      }));
    });

    return () => allListeners.forEach((l) => g.event.removeListener(l));
  }, [
    map,
    pathsPanelMode,
    activeIntersectionId,
    intersections,
    oppositeBlockDraft,
  ]);

  // ── Cleanup on unmount ──
  useEffect(() => () => {
    Object.values(overlaysRef.current).forEach((b) => removeBundle(b));
    overlaysRef.current = {};
  }, []);

  return overlaysRef;
}

function createBundle(g, map) {
  const innerHalo = new g.Circle({ map, clickable: false, zIndex: 11 });
  const middleHalo = new g.Circle({ map, clickable: false, zIndex: 11 });
  const outerHalo = new g.Circle({ map, clickable: false, zIndex: 11 });
  const arrowHalo = new g.Polyline({ map, clickable: false, zIndex: 12 });
  const innerCircle = new g.Circle({ map, clickable: true, draggable: true, cursor: 'move', zIndex: 14 });
  const middleCircle = new g.Circle({ map, clickable: false, zIndex: 13 });
  const outerCircle = new g.Circle({ map, clickable: false, zIndex: 13 });
  const arrow = new g.Polyline({ map, clickable: false, zIndex: 14 });
  const tipHandle = new g.Marker({
    map,
    icon: {
      path: g.SymbolPath.CIRCLE,
      scale: 6,
      fillColor: COLOR_OPPOSITE,
      fillOpacity: 1,
      strokeColor: COLOR_OPPOSITE_HALO,
      strokeWeight: 1.5,
    },
    draggable: true,
    visible: false,
    zIndex: 20,
    cursor: 'grab',
  });
  return {
    innerCircle,
    middleCircle,
    outerCircle,
    arrow,
    innerHalo,
    middleHalo,
    outerHalo,
    arrowHalo,
    tipHandle,
  };
}

function removeBundle(bundle) {
  if (!bundle) return;
  // setVisible(false) up front guarantees the overlay is gone visually even
  // on Maps versions where setMap(null) defers the removal to the next paint.
  const overlays = [
    bundle.innerCircle,
    bundle.middleCircle,
    bundle.outerCircle,
    bundle.arrow,
    bundle.innerHalo,
    bundle.middleHalo,
    bundle.outerHalo,
    bundle.arrowHalo,
    bundle.tipHandle,
  ];
  overlays.forEach((o) => {
    if (!o) return;
    try { o.setVisible?.(false); } catch (_) { /* not supported on every overlay type */ }
    o.setMap(null);
  });
}

function circleOptsInner(isActive) {
  return {
    strokeColor: COLOR_OPPOSITE,
    strokeWeight: STROKE_WEIGHT_MAIN,
    strokeOpacity: 1,
    fillColor: COLOR_OPPOSITE,
    fillOpacity: 1,
    clickable: isActive,
    draggable: isActive,
    zIndex: 14,
    cursor: isActive ? 'move' : undefined,
  };
}
function circleOptsRing() {
  return {
    strokeColor: COLOR_OPPOSITE,
    strokeWeight: STROKE_WEIGHT_MAIN,
    strokeOpacity: 1,
    fillOpacity: 0,
  };
}
function arrowOpts() {
  const g = window.google?.maps;
  const arrowSymbol = g ? {
    path: g.SymbolPath.FORWARD_CLOSED_ARROW,
    scale: 3.5,
    strokeColor: COLOR_OPPOSITE_HALO,
    strokeWeight: 1,
    fillColor: COLOR_OPPOSITE,
    fillOpacity: 1,
  } : null;
  return {
    strokeColor: COLOR_OPPOSITE,
    strokeWeight: STROKE_WEIGHT_MAIN,
    strokeOpacity: 1,
    icons: arrowSymbol ? [{ icon: arrowSymbol, offset: '100%' }] : [],
  };
}
function haloOpts(isActive) {
  return {
    visible: isActive,
    strokeColor: COLOR_OPPOSITE_HALO,
    strokeWeight: STROKE_WEIGHT_HALO,
    strokeOpacity: isActive ? 1 : 0,
    fillOpacity: 0,
  };
}
function arrowHaloOpts(isActive) {
  return {
    visible: isActive,
    strokeColor: COLOR_OPPOSITE_HALO,
    strokeWeight: STROKE_WEIGHT_HALO,
    strokeOpacity: isActive ? 1 : 0,
    icons: [],
  };
}
